/**
 * Orchestrateur d'upload PDF — étape 2 du flow 3-temps.
 *
 * Ce module ne fait QUE le PUT direct vers le bucket Supabase Storage.
 * Les étapes 1 (initiate) et 3 (complete) sont gérées par les hooks
 * `useInitiateFileUpload` / `useCompleteFileUpload` dans `lib/queries/files.ts`.
 *
 * Pourquoi un module séparé ?
 *  - Pas de dépendance React → testable en pur Node/jsdom.
 *  - Pas de dépendance à `@kollectivia/shared` → un import léger côté UI.
 *  - On peut le piloter depuis n'importe où (uploads en lot, retry, drag-drop).
 *
 * Le PUT vers la signed URL N'A PAS BESOIN du token JWT (Supabase Storage
 * délègue l'auth à la signature dans l'URL elle-même), c'est pourquoi on
 * n'utilise pas le client `api` (qui injecterait un Authorization header
 * que le bucket réjècterait).
 */

export type UploadToBucketParams = {
  url: string;
  /**
   * Headers minimaux exigés par le bucket. L'API renvoie au moins
   * `Content-Type: application/pdf` — on ne fait que les forwarder.
   */
  headers: Record<string, string>;
  /**
   * Le binaire à uploader. `File` (drag-drop UI) ou `Blob` (tests).
   */
  body: File | Blob;
  /**
   * Optionnel — si fourni, l'upload peut être annulé (ex: navigation).
   */
  signal?: AbortSignal;
};

export class UploadToBucketError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly responseBody: string,
  ) {
    super(`Upload bucket failed: ${status} ${statusText}`);
    this.name = "UploadToBucketError";
  }
}

/**
 * PUT direct vers la signed URL retournée par `/v1/files/uploads/initiate`.
 * Throws `UploadToBucketError` en cas d'échec (status non-2xx).
 *
 * Note : on ne lit PAS la réponse en JSON parce que Supabase Storage répond
 * généralement avec un body vide ou XML S3-compatible — on se contente du
 * status code.
 */
export async function uploadToBucket(params: UploadToBucketParams): Promise<void> {
  const { url, headers, body, signal } = params;

  const res = await fetch(url, {
    method: "PUT",
    headers,
    body,
    signal,
  });

  if (!res.ok) {
    // On essaie de lire le body texte pour le diagnostic — ne JAMAIS faire
    // ça en succès parce que Storage stream la réponse vide et `.text()`
    // bloque jusqu'au close.
    let responseBody = "";
    try {
      responseBody = await res.text();
    } catch {
      /* ignore — diagnostic only */
    }
    throw new UploadToBucketError(res.status, res.statusText, responseBody);
  }
}

/**
 * Calcule le SHA-256 d'un fichier côté navigateur — `crypto.subtle` est
 * disponible sous toute origine HTTPS et localhost. Utilisé en option pour
 * passer `sha256` à `/initiate` (et le re-confirmer à `/complete`).
 *
 * Attention : streaming sur File volumineux. À surveiller pour PDF >50MB ;
 * en pratique nos plus gros PDF assureurs sont ~30MB → OK.
 */
export async function sha256OfFile(file: File | Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
